import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { useRef } from "react";
import { ArrowRight, Factory, Truck, Palette, Timer, PoundSterling, PhoneCall } from "lucide-react";
import { SEO } from "@/components/site/SEO";
import { Reveal, Overline, SectionHead, Ribbon, CTABand } from "@/components/site/Shared";
import { FAQSection } from "@/components/site/FAQ";
import { IMAGES, PARTNERS, POWDER } from "@/data/site";
import { PRODUCTS } from "@/data/products";

const heroWords = ["Factory", "Direct.", "Fabricated", "In", "Romford."];

const Hero = () => (
  <section className="relative min-h-[92vh] flex items-end bg-[#0A121F] overflow-hidden" data-testid="home-hero">
    <img
      src={IMAGES.heroBifold}
      alt="Aluminium bifold doors fabricated by Empire Aluminium Windows opening onto a garden"
      className="absolute inset-0 w-full h-full object-cover opacity-45"
    />
    <div className="absolute inset-0 bg-gradient-to-t from-[#0A121F] via-[#0A121F]/60 to-transparent" aria-hidden="true" />
    <div className="relative max-w-7xl mx-auto px-6 md:px-12 pb-20 md:pb-28 pt-40 w-full">
      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="font-mono text-xs md:text-sm tracking-[0.25em] uppercase text-[#29CC9A] mb-6"
      >
        Aluminium Windows · Doors · Bifolds · Shopfronts
      </motion.p>
      <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-black tracking-tighter uppercase text-white leading-[0.95]">
        {heroWords.map((w, i) => (
          <span key={w} className="inline-block overflow-hidden mr-4 align-top">
            <motion.span
              className={`inline-block ${i === 1 ? "text-[#29CC9A]" : ""}`}
              initial={{ y: "110%" }}
              animate={{ y: 0 }}
              transition={{ duration: 0.8, delay: 0.35 + i * 0.09, ease: [0.22, 1, 0.36, 1] }}
            >
              {w}
            </motion.span>
          </span>
        ))}
      </h1>
      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 1 }}
        className="mt-7 text-base md:text-lg text-slate-300 max-w-2xl leading-relaxed"
      >
        We fabricate aluminium windows, doors, bifolds, partitions and shopfronts in our own Romford factory — with in-house
        powder coating, full supply chain control and lead times the merchants can't touch. No middleman. No hassle. Unbeatable prices.
      </motion.p>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 1.15 }}
        className="mt-10 flex flex-wrap gap-4"
      >
        <Link
          to="/trade-supply"
          className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#29CC9A] text-[#0A121F] font-bold rounded-full hover:bg-[#22b487] transition-colors duration-300"
          data-testid="hero-trade-btn"
        >
          Trade Supply Only <ArrowRight size={17} />
        </Link>
        <Link
          to="/supply-installation"
          className="inline-flex items-center gap-2 px-7 py-3.5 border border-white/30 text-white font-bold rounded-full hover:bg-white/10 transition-colors duration-300"
          data-testid="hero-install-btn"
        >
          Supply & Installation
        </Link>
      </motion.div>
    </div>
  </section>
);

const stats = [
  { value: "5–10", label: "Day lead times on windows & bifolds" },
  { value: "100%", label: "Fabricated in our own UK factory" },
  { value: "200+", label: "RAL colours powder coated in-house" },
  { value: "0", label: "Middlemen between you and the factory" },
];

const Stats = () => (
  <section className="bg-[#F8FAFC] border-b border-slate-200" data-testid="home-stats">
    <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-2 lg:grid-cols-4">
      {stats.map((s, i) => (
        <Reveal key={s.label} delay={i * 0.08} className="py-12 md:py-16 px-4 border-r border-slate-200 last:border-r-0 first:pl-0">
          <p className="font-heading text-4xl md:text-6xl font-black tracking-tighter text-[#215389]">{s.value}</p>
          <p className="mt-3 text-sm text-slate-600 leading-snug">{s.label}</p>
        </Reveal>
      ))}
    </div>
  </section>
);

const Partners = () => (
  <section className="py-20 md:py-24 bg-white border-b border-slate-200" data-testid="home-partners">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <SectionHead overline="Approved Fabricator" title="Systems we work with" className="max-w-2xl" />
      <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
        {PARTNERS.map((p, i) => (
          <Reveal key={p.id} delay={i * 0.08}>
            <div className="h-full p-8 rounded-2xl bg-[#F8FAFC] border border-slate-200 hover:-translate-y-1 hover:shadow-lg transition-[transform,box-shadow] duration-300" data-testid={`partner-card-${p.id}`}>
              <div className="h-16 flex items-center px-4 bg-white rounded-xl border border-slate-100 w-fit">
                <img src={p.logo} alt={`${p.name} logo`} className="max-h-10 max-w-[190px] object-contain" loading="lazy" decoding="async" />
              </div>
              <h3 className="font-heading mt-5 text-base font-bold text-[#0A121F]">{p.name}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{p.blurb}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const SplitPath = () => (
  <section className="grid grid-cols-1 lg:grid-cols-3" data-testid="home-split-path">
    <Link to="/trade-supply" className="group relative overflow-hidden bg-[#0F1A28] min-h-[440px] flex items-end" data-testid="split-trade-link">
      <img src={IMAGES.welder} alt="Fabricator welding aluminium frames at the Empire factory" className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:scale-105 transition-transform duration-700" loading="lazy" decoding="async" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0F1A28] to-transparent" aria-hidden="true" />
      <div className="relative p-10 md:p-12">
        <Overline light>For Tradesmen & Builders</Overline>
        <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-white">Trade Supply<br />Only</h2>
        <p className="mt-4 text-slate-300 text-sm md:text-base max-w-sm leading-relaxed">
          Straight-from-factory pricing, transparent lead times and product availability you can build a schedule around.
        </p>
        <span className="mt-6 inline-flex items-center gap-2 text-[#29CC9A] font-bold text-sm group-hover:gap-4 transition-all duration-300">
          Explore trade supply <ArrowRight size={16} />
        </span>
      </div>
    </Link>
    <Link to="/supply-installation" className="group relative overflow-hidden bg-[#215389] min-h-[440px] flex items-end" data-testid="split-install-link">
      <img src={IMAGES.glassFront} alt="Completed aluminium glazing installation on a modern home" className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:scale-105 transition-transform duration-700" loading="lazy" decoding="async" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#215389] to-transparent" aria-hidden="true" />
      <div className="relative p-10 md:p-12">
        <Overline light>For Homeowners</Overline>
        <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-white">Supply &<br />Installation</h2>
        <p className="mt-4 text-slate-200 text-sm md:text-base max-w-sm leading-relaxed">
          One team from survey to sign-off. Fabricated and fitted by the people who made it — a complete, no-hassle service.
        </p>
        <span className="mt-6 inline-flex items-center gap-2 text-[#29CC9A] font-bold text-sm group-hover:gap-4 transition-all duration-300">
          Explore installation <ArrowRight size={16} />
        </span>
      </div>
    </Link>
    <Link to="/products?category=Commercial" className="group relative overflow-hidden bg-[#0A121F] min-h-[440px] flex items-end" data-testid="split-commercial-link">
      <img src={IMAGES.curtainWalling} alt="Aluminium curtain walling on a UK commercial building fabricated by Empire" className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:scale-105 transition-transform duration-700" loading="lazy" decoding="async" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A121F] to-transparent" aria-hidden="true" />
      <div className="relative p-10 md:p-12">
        <Overline light>For Construction Companies</Overline>
        <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-white">Commercial &<br />Construction</h2>
        <p className="mt-4 text-slate-300 text-sm md:text-base max-w-sm leading-relaxed">
          Curtain walling, commercial windows, doors and shopfronts on Comar & Senior systems — delivered to programme.
        </p>
        <span className="mt-6 inline-flex items-center gap-2 text-[#29CC9A] font-bold text-sm group-hover:gap-4 transition-all duration-300">
          Explore commercial <ArrowRight size={16} />
        </span>
      </div>
    </Link>
  </section>
);

const bentoSpans = [
  "md:col-span-7 md:row-span-2",
  "md:col-span-5",
  "md:col-span-5",
  "md:col-span-4",
  "md:col-span-4",
  "md:col-span-4",
];

const ProductBento = () => (
  <section className="py-24 md:py-32 bg-[#F8FAFC]" data-testid="home-products">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="flex flex-wrap items-end justify-between gap-6 mb-14">
        <SectionHead overline="What We Fabricate" title="Windows, doors & everything aluminium" />
        <Link to="/products" className="inline-flex items-center gap-2 font-bold text-sm text-[#215389] hover:gap-3 transition-all duration-300" data-testid="home-all-products-link">
          All 13 product ranges <ArrowRight size={16} />
        </Link>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-5 auto-rows-[240px]">
        {PRODUCTS.slice(0, 6).map((p, i) => (
          <Link
            key={p.slug}
            to={`/products/${p.slug}`}
            className={`group relative overflow-hidden rounded-2xl bg-[#0F1A28] ${bentoSpans[i]}`}
            data-testid={`home-product-card-${p.slug}`}
          >
            <img src={p.image} alt={p.alt} className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-55 group-hover:scale-105 transition-[opacity,transform] duration-700" loading="lazy" decoding="async" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A121F]/90 via-transparent to-transparent" aria-hidden="true" />
            <div className="absolute bottom-0 left-0 p-6">
              <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#29CC9A]">{p.category}</p>
              <h3 className="font-heading text-xl md:text-2xl font-bold text-white mt-1">{p.name}</h3>
            </div>
          </Link>
        ))}
      </div>
    </div>
  </section>
);

const whyItems = [
  { icon: Factory, title: "Fabricated straight from factory", text: "Every frame is cut, crimped and glazed in our Romford facility. You buy from the people who make it — full supply chain control, zero markup layers." },
  { icon: Palette, title: "In-house powder coating", text: "Our own coating line means any RAL colour, dual colour and bespoke finishes without third-party delays. Colour never holds up your job." },
  { icon: Timer, title: "Clear, fast lead times", text: "Windows and bifolds in 5–10 working days. We publish lead times and hit them — because we control every stage of production." },
  { icon: PoundSterling, title: "Unbeatable pricing", text: "No middleman means trade prices the merchants can't match. Compare any like-for-like quote and see the difference." },
  { icon: Truck, title: "Fast delivery, no hassle", text: "Our own transport delivers across London and the surrounding areas. Kerbside or site offload, on the day we promised." },
  { icon: PhoneCall, title: "Direct access to the team", text: "Call the factory and speak to the fabricator handling your order — not a call centre. Changes and questions sorted in minutes." },
];

const Factory_ = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);

  return (
    <section ref={ref} className="relative py-24 md:py-32 bg-[#0F1A28] overflow-hidden" data-testid="home-why-us">
      <motion.img
        src={IMAGES.machinery}
        alt=""
        aria-hidden="true"
        style={{ y }}
        className="absolute inset-0 w-full h-[120%] object-cover opacity-15"
      />
      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <SectionHead light overline="Why Empire" title="The tradesman's preferred choice" className="max-w-2xl" />
        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {whyItems.map((item, i) => (
            <Reveal key={item.title} delay={i * 0.06}>
              <div className="h-full p-8 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm hover:-translate-y-1 hover:bg-white/[0.08] transition-[transform,background-color] duration-300">
                <item.icon size={26} className="text-[#29CC9A]" />
                <h3 className="font-heading mt-5 text-lg font-bold text-white">{item.title}</h3>
                <p className="mt-3 text-sm text-slate-400 leading-relaxed">{item.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const ral = [
  { name: "RAL 7016 Anthracite", hex: "#383E42" },
  { name: "RAL 9005 Jet Black", hex: "#0E0E10" },
  { name: "RAL 9910 White", hex: "#F1EFE2" },
  { name: "RAL 7021 Black Grey", hex: "#2F3234" },
  { name: "RAL 6005 Moss Green", hex: "#0F4336" },
  { name: "RAL 5011 Steel Blue", hex: "#232D53" },
  { name: "RAL 8022 Black Brown", hex: "#1A1719" },
  { name: "RAL 9006 Aluminium", hex: "#A1A1A0" },
];

const Colours = () => (
  <section className="py-24 md:py-32 bg-[#F8FAFC]" data-testid="home-colours">
    <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
      <Reveal>
        <Overline>In-House Powder Coating · Paramount Powders</Overline>
        <h2 className="font-heading text-3xl md:text-5xl font-bold tracking-tight text-[#0A121F]">Any colour.<br />Bespoke to your build.</h2>
        <p className="mt-6 text-base md:text-lg text-slate-700 leading-relaxed max-w-lg">
          Our in-house coating line runs premium powders from{" "}
          <a href={POWDER.chartUrl} target="_blank" rel="noopener noreferrer" className="font-bold text-[#215389] underline decoration-[#29CC9A] decoration-2 underline-offset-4 hover:text-[#29CC9A] transition-colors duration-200" data-testid="paramount-ral-link">
            Paramount Powders
          </a>{" "}
          — choose from the full RAL chart in gloss, matt or semi-gloss. Because coating happens under our own roof, bespoke colour never holds up your job.
        </p>
        <div className="mt-8">
          <p className="font-mono text-xs tracking-[0.2em] uppercase text-[#215389] mb-4">Standard stock colours — Gloss · Matt · Semi-Gloss</p>
          <div className="grid grid-cols-3 gap-3 max-w-md" data-testid="stock-colours">
            {POWDER.stock.map((c) => (
              <div key={c.name} className="group">
                <div className="aspect-[4/3] rounded-xl border-2 border-[#29CC9A]/40 group-hover:-translate-y-1 transition-transform duration-300" style={{ backgroundColor: c.hex }} />
                <p className="mt-2 text-[11px] font-bold text-slate-600 leading-tight">{c.name}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="mt-8 grid grid-cols-4 gap-3 max-w-md">
          {ral.map((c) => (
            <div key={c.name} className="group">
              <div className="aspect-square rounded-xl border border-slate-200 group-hover:-translate-y-1 transition-transform duration-300" style={{ backgroundColor: c.hex }} />
              <p className="mt-2 text-[10px] font-mono text-slate-500 leading-tight">{c.name}</p>
            </div>
          ))}
        </div>
        <div className="mt-8 flex flex-wrap gap-4 items-center">
          <Link
            to="/colour-visualiser"
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#215389] text-white text-sm font-bold rounded-full hover:bg-[#1a4470] transition-colors duration-300"
            data-testid="home-visualiser-link"
          >
            Try the Colour Visualiser <ArrowRight size={16} />
          </Link>
          <a href={POWDER.chartUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm font-bold text-[#215389] hover:gap-3 transition-all duration-300" data-testid="full-ral-chart-link">
            Full Paramount RAL chart <ArrowRight size={15} />
          </a>
        </div>
      </Reveal>
      <Reveal delay={0.15}>
        <div className="relative">
          <img src={IMAGES.powderCoating} alt="Aluminium profiles being powder coated on Empire's in-house coating line" className="rounded-2xl w-full object-cover aspect-[4/3]" loading="lazy" decoding="async" />
          <div className="absolute -bottom-6 -left-6 bg-[#29CC9A] text-[#0A121F] rounded-2xl px-6 py-5 font-heading font-bold text-sm md:text-base shadow-xl">
            Coated & cured<br />under our own roof
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

const quotes = [
  { text: "Empire turn my bifolds around in a week when everyone else quotes a month. Prices are silly good because there's no middleman — it's factory direct.", who: "James M.", role: "Builder, Essex" },
  { text: "As a window fitter I've used every supplier in the South East. Empire's lead times are the only ones I'd put in a contract.", who: "Dariusz K.", role: "Installer, East London" },
  { text: "Needed a bespoke RAL colour for a development of nine plots. Coated in-house, delivered on schedule, every frame perfect.", who: "Sophie R.", role: "Site Manager, Kent" },
];

const Testimonials = () => (
  <section className="py-24 md:py-32 bg-white" data-testid="home-testimonials">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <SectionHead overline="Word On Site" title="Trusted by the trade" />
      <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-5">
        {quotes.map((q, i) => (
          <Reveal key={q.who} delay={i * 0.08}>
            <figure className="h-full p-8 rounded-2xl bg-[#F8FAFC] border border-slate-200 hover:-translate-y-1 hover:shadow-lg transition-[transform,box-shadow] duration-300">
              <p className="text-[#29CC9A] font-heading text-4xl font-black leading-none">"</p>
              <blockquote className="mt-2 text-sm md:text-base text-slate-700 leading-relaxed">{q.text}</blockquote>
              <figcaption className="mt-6">
                <p className="font-bold text-[#0A121F] text-sm">{q.who}</p>
                <p className="text-xs text-slate-500">{q.role}</p>
              </figcaption>
            </figure>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const HOME_FAQS = [
  { q: "How quickly can you fabricate aluminium windows and bifold doors?", a: "Because everything is made in our own Romford factory, aluminium windows and bifold doors are typically fabricated in 5–10 working days. Commercial systems and curtain walling run 10–20 working days. Lead times are confirmed on your order acknowledgement and we hit them." },
  { q: "Do you offer supply only for the trade?", a: "Yes — trade supply only is a core part of our business. Tradesmen, builders and installers buy factory-direct with no middleman, so prices are consistently lower than merchants on like-for-like quality. Volume and repeat-trade rates are available." },
  { q: "What colours can I have my aluminium windows or doors in?", a: "Any RAL colour. Our in-house powder coating line runs Paramount Powders across the full RAL chart in gloss, matt and semi-gloss finishes. Jet Black (RAL 9005), Traffic White (RAL 9016) and Anthracite Grey (RAL 7016) are held in stock. Dual colour inside/outside is also available." },
  { q: "Which areas do you cover?", a: "We're based in Romford (RM5) and supply and install across London and the surrounding areas. Supply-only orders can be delivered nationwide for trade customers." },
  { q: "Which aluminium systems do you fabricate?", a: "We're an approved fabricator for Comar Architectural Aluminium Systems (5P.i windows, 7P.i doors, 6EFT curtain walling), Senior Architectural Systems (SPD150 doors, SF52 curtain walling, PURe range) and Exlabesa residential window and door systems." },
  { q: "Can homeowners buy directly from the factory?", a: "Absolutely. Homeowners get the same factory-direct pricing through our full supply & installation service — free survey, in-house fabrication and fitting by our own installers, all under one roof." },
];

const AREAS = ["Romford", "East London", "Ilford", "Brentwood", "Chelmsford", "Basildon", "Dagenham", "Enfield", "Greater London", "Upminster", "Hornchurch", "Loughton"];

const SeoContent = () => (
  <section className="py-24 md:py-32 bg-white border-t border-slate-200" data-testid="home-seo-content">
    <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-14">
      <Reveal>
        <Overline>UK Aluminium Specialists</Overline>
        <h2 className="font-heading text-3xl md:text-5xl font-bold tracking-tight text-[#0A121F]">Factory-direct aluminium, wherever you build</h2>
        <p className="mt-6 text-base text-slate-700 leading-relaxed">
          Empire Aluminium Windows is a UK aluminium fabricator manufacturing{" "}
          <Link to="/products/aluminium-windows" className="font-semibold text-[#215389] hover:underline">aluminium windows</Link>,{" "}
          <Link to="/products/bifold-doors" className="font-semibold text-[#215389] hover:underline">bifold doors</Link>,{" "}
          <Link to="/products/curtain-walling" className="font-semibold text-[#215389] hover:underline">curtain walling</Link>,{" "}
          <Link to="/products/shopfronts" className="font-semibold text-[#215389] hover:underline">shopfronts</Link> and{" "}
          <Link to="/products/commercial-doors" className="font-semibold text-[#215389] hover:underline">commercial aluminium doors</Link>{" "}
          at our Romford factory. Whether you're a tradesman buying{" "}
          <Link to="/trade-supply" className="font-semibold text-[#215389] hover:underline">supply only at trade prices</Link>, a homeowner after a complete{" "}
          <Link to="/supply-installation" className="font-semibold text-[#215389] hover:underline">supply and installation service</Link>, or a construction company procuring commercial glazing packages to programme — you buy straight from the factory floor. Homeowners can price{" "}
          <Link to="/bifold-doors" className="font-semibold text-[#215389] hover:underline" data-testid="home-bifold-landing-link">aluminium bifold doors per sash</Link>{" "}
          and design their doors online in under a minute.
        </p>
        <p className="mt-4 text-base text-slate-700 leading-relaxed">
          Full supply chain control means published lead times, in-house powder coating in any RAL colour, and pricing no merchant can match. That's why builders, main contractors and homeowners across the South East make Empire their first call.
        </p>
      </Reveal>
      <Reveal delay={0.12}>
        <h3 className="font-heading text-xl md:text-2xl font-bold text-[#0A121F]">Areas we serve</h3>
        <p className="mt-4 text-base text-slate-700 leading-relaxed">
          From our factory at Northgate Industrial Estate, Romford (RM5 2BG), our own transport delivers and our teams install across:
        </p>
        <div className="mt-6 flex flex-wrap gap-2.5" data-testid="areas-served">
          {AREAS.map((a) => (
            <span key={a} className="px-4 py-2 rounded-full bg-[#F8FAFC] border border-slate-200 text-sm font-semibold text-slate-700">{a}</span>
          ))}
        </div>
        <p className="mt-6 text-sm text-slate-500">Nationwide delivery available on trade supply-only orders.</p>
        <Link to="/contact" className="mt-6 inline-flex items-center gap-2 font-bold text-sm text-[#215389] hover:gap-3 transition-all duration-300" data-testid="areas-contact-link">
          Check we cover your postcode <ArrowRight size={15} />
        </Link>
      </Reveal>
    </div>
  </section>
);

export default function Home() {
  return (
    <main data-testid="home-page">
      <SEO
        title="Aluminium Bifold Doors & Windows Romford | From £475 Per Sash"
        description="Aluminium bifold doors factory direct from Romford — from £475 per sash, supply. Installation available with a 10-year guarantee. Windows & doors too."
        path="/"
        faqs={HOME_FAQS}
      />
      <Hero />
      <Ribbon />
      <Stats />
      <Partners />
      <SplitPath />
      <ProductBento />
      <Factory_ />
      <Colours />
      <Testimonials />
      <FAQSection faqs={HOME_FAQS} />
      <SeoContent />
      <CTABand />
    </main>
  );
}
